import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { useMemo } from 'react';

import type { ContactVerificationChannel } from '@repo/common';
import {
  ContactVerificationSiteKeyDocument,
  MySmsMarketingStatusDocument,
  type MySmsMarketingStatusQuery,
  OptOutOfSmsMarketingDocument,
  RequestMySmsMarketingCodeDocument,
  ResendMySmsMarketingCodeDocument,
  type SmsMarketingAccountSource,
  SmsMarketingConsentState,
  VerifyMySmsMarketingCodeDocument,
} from '@repo/frontend-shared';

import { requestContactVerificationBotToken } from './contact-verification-bot';
import { toCodeRequestResult, toPendingVerification, toVerifyResult } from './sms-marketing-graphql';
import type {
  SmsMarketingCodeRequestResult,
  SmsMarketingPendingVerification,
  SmsMarketingSignupRequest,
  SmsMarketingVerifyResult,
} from './sms-marketing-signup-types';

export type SmsMarketingAccountStatus = MySmsMarketingStatusQuery['mySmsMarketingStatus'];

function isSubscribed(status: SmsMarketingAccountStatus | undefined): boolean {
  return status?.consent === SmsMarketingConsentState.ACTIVE;
}

export interface SmsMarketingAccount {
  status: SmsMarketingAccountStatus | undefined;
  pendingVerification: SmsMarketingPendingVerification | undefined;
  isLoading: boolean;
  hasError: boolean;
  isOptingOut: boolean;
  reload: () => Promise<void>;
  submit: (request: SmsMarketingSignupRequest) => Promise<SmsMarketingCodeRequestResult>;
  verify: (code: string) => Promise<SmsMarketingVerifyResult>;
  resend: (channel?: ContactVerificationChannel) => Promise<SmsMarketingCodeRequestResult>;
  optOut: () => Promise<void>;
}

/** The signed-in person's number, verification and consent, with the calls the signup component needs. */
export const useSmsMarketingAccount = (source: SmsMarketingAccountSource): SmsMarketingAccount => {
  const { data, loading, error, refetch } = useQuery(MySmsMarketingStatusDocument, {
    fetchPolicy: 'cache-and-network',
  });
  const [requestCode] = useMutation(RequestMySmsMarketingCodeDocument);
  const [loadSiteKey] = useLazyQuery(ContactVerificationSiteKeyDocument, { fetchPolicy: 'network-only' });
  async function botToken(): Promise<string | undefined> {
    const { data } = await loadSiteKey();
    if (!data) throw new Error('Verification configuration unavailable');
    return requestContactVerificationBotToken(data.contactVerificationSiteKey);
  }
  const [verifyCode] = useMutation(VerifyMySmsMarketingCodeDocument);
  const [resendCode] = useMutation(ResendMySmsMarketingCodeDocument);
  const [optOutMutation, { loading: isOptingOut }] = useMutation(OptOutOfSmsMarketingDocument, {
    update(cache, { data: result }) {
      if (result) {
        cache.writeQuery({
          query: MySmsMarketingStatusDocument,
          data: { __typename: 'Query', mySmsMarketingStatus: result.optOutOfSmsMarketing },
        });
      }
    },
  });
  const status = data?.mySmsMarketingStatus;
  // Apollo keeps the same object while the data is unchanged, so the signup does not restart its code step on every render.
  const pendingVerification = useMemo(() => toPendingVerification(status?.pendingCode), [status?.pendingCode]);

  async function reload(): Promise<void> {
    await refetch();
  }

  /** Runs after a call that already succeeded, so a failed reload only leaves the status stale. */
  async function reloadAfterSuccess(): Promise<SmsMarketingAccountStatus | undefined> {
    try {
      const { data: reloaded } = await refetch();
      return reloaded?.mySmsMarketingStatus;
    } catch (error) {
      console.warn('[useSmsMarketingAccount] status reload failed', error);
      return undefined;
    }
  }

  async function submit(request: SmsMarketingSignupRequest): Promise<SmsMarketingCodeRequestResult> {
    const token = await botToken();
    const { data: result } = await requestCode({
      variables: {
        input: {
          phone: request.phoneE164,
          consent: request.consent,
          disclosureVersion: request.disclosureVersion,
          source,
          botToken: token,
        },
      },
    });
    if (!result) {
      throw new Error('requestMySmsMarketingCode returned no data');
    }
    const outcome = toCodeRequestResult(result.requestMySmsMarketingCode);
    if (outcome.status === 'confirmed') {
      return { status: 'confirmed', subscribed: isSubscribed(await reloadAfterSuccess()) };
    }
    return outcome;
  }

  async function verify(code: string): Promise<SmsMarketingVerifyResult> {
    const { data: result } = await verifyCode({ variables: { input: { code } } });
    if (!result) {
      throw new Error('verifyMySmsMarketingCode returned no data');
    }
    const outcome = toVerifyResult(result.verifyMySmsMarketingCode);
    if (outcome.status === 'failed') {
      return outcome;
    }
    const reloaded = await reloadAfterSuccess();
    return outcome.status === 'verified' ? { status: 'verified', subscribed: isSubscribed(reloaded) } : outcome;
  }

  async function resend(channel: ContactVerificationChannel = 'sms'): Promise<SmsMarketingCodeRequestResult> {
    const token = await botToken();
    const { data: result } = await resendCode({ variables: { input: { channel, botToken: token } } });
    if (!result) {
      throw new Error('resendMySmsMarketingCode returned no data');
    }
    return toCodeRequestResult(result.resendMySmsMarketingCode);
  }

  async function optOut(): Promise<void> {
    await optOutMutation();
  }

  return {
    status,
    pendingVerification,
    isLoading: loading && !status,
    hasError: Boolean(error) && !status,
    isOptingOut,
    reload,
    submit,
    verify,
    resend,
    optOut,
  };
};
